import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * BeforeAfter — comparateur avant/après premium avec slider drag.
 * - Pointer Events unifiés + setPointerCapture : drag continu souris ET tactile,
 *   même si le pointeur sort de la barre ou du composant.
 * - Découpage par clip-path (GPU) : aucun recalcul de layout, 60 fps constants.
 * - Aucune transition CSS pendant le drag : la barre suit le curseur à l'instant.
 * - Poignée cyan centrale draggable sur toute la largeur (0 → 100 %).
 * - Labels AVANT / APRÈS glass-morphism en surimpression.
 * - Animation d'entrée : balayage lent quand la section entre à l'écran.
 * - Respect strict de prefers-reduced-motion.
 *
 * Usage :
 *   <BeforeAfter
 *      beforeSrc="/photos/before-after/parquet-avant.jpg"
 *      afterSrc="/photos/before-after/parquet-apres.jpg"
 *      alt="Parquet — rénovation complète"
 *   />
 */
export default function BeforeAfter({ beforeSrc, afterSrc, alt = "", initial = 50, testId }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(initial);
  const draggingRef = useRef(false);
  const introPlayedRef = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    setPos(p);
  }, []);

  const onPointerDown = (e) => {
    // Capture du pointeur : tous les pointermove suivants arrivent sur CE composant,
    // même si le curseur/doigt sort de la zone. Vrai drag continu.
    draggingRef.current = true;
    introPlayedRef.current = true; // l'utilisateur prend la main : stopper l'intro
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      /* navigateur ancien sans capture : le drag reste fonctionnel */
    }
    setFromClientX(e.clientX);
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    setFromClientX(e.clientX);
  };

  const endDrag = (e) => {
    draggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) {}
  };

  // Animation d'entrée au scroll : sweep 50 → 0 → 100 → 50 sur ~2,6s
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const el = containerRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !introPlayedRef.current) {
            introPlayedRef.current = true;
            const t0 = performance.now();
            const duration = 2600;
            const from = initial;
            const step = (t) => {
              if (draggingRef.current) return; // ne pas gêner l'utilisateur
              const p = Math.min(1, (t - t0) / duration);
              const wave = Math.sin(p * Math.PI * 2) * 35;
              const value = Math.min(100, Math.max(0, from + wave));
              setPos(value);
              if (p < 1) requestAnimationFrame(step);
              else setPos(initial);
            };
            requestAnimationFrame(step);
            io.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [initial]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 3));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 3));
    if (e.key === "Home") setPos(0);
    if (e.key === "End") setPos(100);
  };

  return (
    <div
      ref={containerRef}
      data-testid={testId || "before-after"}
      role="slider"
      tabIndex={0}
      aria-label={`Comparateur avant après${alt ? " — " + alt : ""}`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onContextMenu={(e) => e.preventDefault()}
      className="nx-ba"
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "3/2",
        overflow: "hidden",
        borderRadius: 12,
        cursor: "ew-resize",
        // Pas de sélection de texte ni de drag d'image parasite pendant le drag
        userSelect: "none",
        WebkitUserSelect: "none",
        // pan-y : le scroll vertical reste possible sur mobile, le drag horizontal
        // est entièrement dédié au slider (aucun geste tactile parasite)
        touchAction: "pan-y",
        boxShadow: "0 24px 48px -20px rgba(13,43,69,0.35)",
        background: "#0D2B45",
      }}
    >
      {/* APRÈS — image de fond (100%) */}
      <img
        src={afterSrc}
        alt={alt ? `${alt} — après` : "Après"}
        draggable={false}
        loading="lazy"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          pointerEvents: "none",
        }}
      />

      {/* AVANT — image clippée par clip-path (GPU, aucun recalcul de layout) */}
      <img
        src={beforeSrc}
        alt={alt ? `${alt} — avant` : "Avant"}
        draggable={false}
        loading="lazy"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          pointerEvents: "none",
          clipPath: `inset(0 ${100 - pos}% 0 0)`,
          willChange: "clip-path",
        }}
      />

      {/* Labels AVANT / APRÈS glass-morphism */}
      <div
        style={{
          position: "absolute",
          top: 16,
          left: 16,
          padding: "6px 14px",
          background: "rgba(13, 43, 69, 0.75)",
          backdropFilter: "blur(8px)",
          color: "#fff",
          fontFamily: "Exo 2, Arial, sans-serif",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.14em",
          borderRadius: 4,
          borderLeft: "3px solid #00C8FF",
          pointerEvents: "none",
        }}
      >
        AVANT
      </div>
      <div
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          padding: "6px 14px",
          background: "rgba(0, 200, 255, 0.92)",
          backdropFilter: "blur(8px)",
          color: "#0D2B45",
          fontFamily: "Exo 2, Arial, sans-serif",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.14em",
          borderRadius: 4,
          pointerEvents: "none",
        }}
      >
        APRÈS
      </div>

      {/* Ligne verticale du slider + poignée */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: `${pos}%`,
          width: 3,
          background: "#00C8FF",
          transform: "translateX(-50%)",
          boxShadow: "0 0 16px rgba(0,200,255,0.7)",
          pointerEvents: "none",
          willChange: "left",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "50%",
          left: `${pos}%`,
          width: 44,
          height: 44,
          transform: "translate(-50%, -50%)",
          borderRadius: "50%",
          background: "#fff",
          border: "3px solid #00C8FF",
          boxShadow: "0 6px 20px rgba(13,43,69,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          willChange: "left",
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0D2B45" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 6l-6 6 6 6" />
          <path d="M15 6l6 6-6 6" />
        </svg>
      </div>
    </div>
  );
}
